/**
 * Soft gate: admin recovery approve requires the operator mobile brief unless
 * the session reason allows an explicit override with approval notes.
 */

import type { RecoveryReasonCode } from './admin-recovery-types.js';

export const RECOVERY_APPROVE_WITHOUT_SEND_REASON_CODES = [
  'EMERGENCY',
  'EXTERNAL_EVIDENCE',
] as const satisfies readonly RecoveryReasonCode[];

export type RecoveryApproveWithoutSendReasonCode =
  (typeof RECOVERY_APPROVE_WITHOUT_SEND_REASON_CODES)[number];

export interface RecoveryApproveGateResult {
  canApprove: boolean;
  blockReason: string | null;
  /** When true, approval notes unlock approve for never-sent tasks. */
  requiresOverrideNote: boolean;
}

export function isRecoveryApproveWithoutSendReasonCode(
  reasonCode: string,
): reasonCode is RecoveryApproveWithoutSendReasonCode {
  return (RECOVERY_APPROVE_WITHOUT_SEND_REASON_CODES as readonly string[]).includes(reasonCode);
}

export function evaluateRecoveryApproveGate(input: {
  assignmentFound: boolean;
  sessionReasonCode: string;
  resolveNotes?: string | null;
}): RecoveryApproveGateResult {
  if (input.assignmentFound) {
    return { canApprove: true, blockReason: null, requiresOverrideNote: false };
  }

  if (isRecoveryApproveWithoutSendReasonCode(input.sessionReasonCode)) {
    if (input.resolveNotes?.trim()) {
      return { canApprove: true, blockReason: null, requiresOverrideNote: true };
    }
    return {
      canApprove: false,
      blockReason:
        'This task was never sent to the operator. For Emergency or External evidence sessions, add approval notes explaining why you are completing without a mobile brief.',
      requiresOverrideNote: true,
    };
  }

  return {
    canApprove: false,
    blockReason:
      'This task was never sent to the operator. Send it via Task Management first, then complete on behalf if needed.',
    requiresOverrideNote: false,
  };
}
